/* eslint-disable react/no-array-index-key */
import {
  ListItemButton,
  ListItemText,
  Collapse,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material';
import { Icon, IconName } from '@fashionnovateam/nova-ui-toolkit';

type SidebarItemProps = {
  label: string;
  icon?: IconName;
  id?: string;
  isActive?: boolean;
  isExpanded?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  showChevron?: boolean;
};

type BaseSidebarItemProps = {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
  sx?: SxProps<Theme>;
};

interface SectionItemProps {
  label: string;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  articles: Array<{ _id: string; title: string }>;
  children?: React.ReactNode;
}

type ArticleItemProps = BaseSidebarItemProps;

export function SectionItem({
  label,
  isActive,
  isExpanded,
  onToggle,
  onNavigate,
  articles,
  children,
}: SectionItemProps) {
  return (
    <>
      <ListItemButton
        selected={isActive}
        disableRipple
        className={`rounded-[10px] flex items-center gap-1 ${
          isActive ? 'text-primary-700' : 'text-base-secondary'
        } font-geist text-sm`}
        sx={{
          '&.Mui-selected': {
            backgroundColor: '#FFFFFF',
          },
        }}
      >
        {articles.length > 0 && (
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle();
            }}
            size="small"
            aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
            className="hover:bg-neutral-100 hover:rounded-[10px]"
          >
            <Icon
              name={
                isExpanded ? 'chevron/chevron-down' : 'chevron/chevron-right'
              }
              className={isActive ? 'text-primary-700' : 'text-base-secondary'}
            />
          </IconButton>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onNavigate();
          }}
          className="flex-grow cursor-pointer text-left bg-transparent border-0 p-0"
          aria-label={`Navigate to ${label} section`}
        >
          <ListItemText
            primary={label}
            sx={{
              '& .MuiListItemText-primary': {
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Geist',
              },
            }}
          />
        </button>
      </ListItemButton>
      {children && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

export function ArticleItem({ label, isActive, onClick }: ArticleItemProps) {
  return (
    <ListItemButton
      onClick={onClick}
      selected={isActive}
      disableRipple
      className={`transition-none ${isActive ? 'text-primary-700' : ''}`}
      sx={{
        padding: '0px 8px',
        transition: 'none',
        '&.Mui-selected': {
          backgroundColor: '#FFFFFF',
          color: '#5247DF',
          borderLeft: '1px solid',
          borderColor: '#5247DF',
          '&:hover': {
            backgroundColor: '#FFFFFF',
          },
        },
        borderLeft: '1px solid',
        borderColor: '#EBEBEB',
        '&:hover': {
          backgroundColor: '#FFFFFF',
        },
      }}
    >
      <ListItemText
        primary={label}
        sx={{
          '& .MuiListItemText-primary': {
            fontSize: '13px',
            fontWeight: 400,
            fontFamily: 'Geist',
            padding: '8px',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '10px',
          },
        }}
      />
    </ListItemButton>
  );
}

export function SidebarItem({
  label,
  icon,
  isActive,
  onClick,
}: Omit<SidebarItemProps, 'isExpanded' | 'children' | 'showChevron'>) {
  return (
    <ListItemButton
      onClick={onClick}
      selected={isActive}
      disableRipple
      className={`transition-none ${isActive ? 'text-primary-700' : 'text-base-secondary'} font-sans text-sm`}
      sx={{
        transition: 'none',
        '&.Mui-selected': {
          backgroundColor: '#FFFFFF',
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          borderRadius: '10px',
        },
      }}
    >
      {icon && (
        <IconButton size="small" edge="start" sx={{ mr: 1 }}>
          <Icon
            name={icon}
            className={isActive ? 'text-primary-700' : 'text-base-secondary'}
          />
        </IconButton>
      )}
      <ListItemText
        primary={label}
        sx={{
          '& .MuiListItemText-primary': {
            fontSize: '14px',
            fontWeight: 500,
            fontFamily: 'Geist',
          },
        }}
      />
    </ListItemButton>
  );
}
